import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Target,
  TrendingUp,
  Award,
  Users,
  Sparkles,
  Download,
  Send,
  CheckCircle
} from 'lucide-react';
import Navigation from '../components/Navigation';

const BecomeSponsorPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Sponsor application:', formData);
    // Handle form submission
  };

  const whySponsor = [
    {
      icon: Target,
      title: 'Exposure',
      description: 'Connect with 10,000+ purpose-driven leaders, creators, and changemakers across three powerful days.'
    },
    {
      icon: TrendingUp,
      title: 'Impact',
      description: 'Align your brand with a movement that champions faith, culture, and purpose in leadership.'
    },
    {
      icon: Award,
      title: 'Legacy',
      description: 'Be remembered as a partner who invested in transformation, not just transactions.'
    }
  ];

  const sponsorshipTiers = [
    {
      tier: 'Gold Partner',
      price: '$25,000+',
      color: '#CFA64B',
      benefits: [
        'Prime stage logo placement',
        'VIP speaker meet & greets',
        '20 complimentary passes',
        'Exclusive booth location',
        'Social media features'
      ]
    },
    {
      tier: 'Silver Partner',
      price: '$10,000 - $24,999',
      color: '#9CA3AF',
      benefits: [
        'Logo on event materials',
        '10 complimentary passes',
        'Standard booth space',
        'Brand mentions in email campaigns'
      ]
    },
    {
      tier: 'Community Partner',
      price: '$5,000 - $9,999',
      color: '#60A5FA',
      benefits: [
        'Logo on website',
        '5 complimentary passes',
        'Recognition in program',
        'Social media shoutout'
      ]
    }
  ];

  const partnerLogos = [
    'PARTNER 1', 'PARTNER 2', 'PARTNER 3', 'PARTNER 4', 'PARTNER 5', 'PARTNER 6'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1920&h=1080&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B]/95 via-[#0B1B3B]/85 to-[#0B1B3B]/75" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Become a <span className="text-[#CFA64B]">Sponsor</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Partner with the movement that's redefining leadership and culture.
          </p>
          <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
            <Download className="w-5 h-5 mr-2" />
            Download Sponsorship Deck
          </Button>
        </div>
      </div>

      {/* Section 1: Why Sponsor KNOWN CON */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Why Sponsor KNOWN CON</h2>
            <p className="text-xl text-gray-600">Connect with purpose-driven audiences and leaders shaping tomorrow</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whySponsor.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card 
                  key={idx}
                  className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] rounded-full flex items-center justify-center transition-colors">
                      <Icon className="w-10 h-10 text-[#CFA64B] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 2: Sponsorship Tiers */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Sponsorship Tiers</h2>
            <p className="text-xl text-gray-600">Choose the partnership level that aligns with your vision</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {sponsorshipTiers.map((tier, idx) => (
              <Card 
                key={idx}
                className="border-2 hover:shadow-2xl transition-all duration-300 relative overflow-hidden"
                style={{ borderColor: tier.color }}
              >
                <div 
                  className="absolute top-0 right-0 w-32 h-32 opacity-5 rounded-bl-full"
                  style={{ backgroundColor: tier.color }}
                />
                <CardContent className="p-8 relative">
                  <div 
                    className="inline-block px-4 py-2 rounded-full mb-6 font-bold text-white"
                    style={{ backgroundColor: tier.color }}
                  >
                    {tier.tier}
                  </div>
                  <div className="text-3xl font-bold text-[#0B1B3B] mb-6">{tier.price}</div>
                  <ul className="space-y-3">
                    {tier.benefits.map((benefit, bidx) => (
                      <li key={bidx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#CFA64B] flex-shrink-0 mt-1" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Section 3: Our Partners */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Our Partners</h2>
            <p className="text-xl text-gray-600">Join the brands making an impact</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {partnerLogos.map((logo, idx) => (
              <div 
                key={idx}
                className="h-24 bg-gray-100 rounded-lg flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 hover:shadow-lg cursor-pointer"
              >
                <span className="text-gray-400 font-bold text-sm">{logo}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section 4: Apply to Sponsor */}
      <div className="py-20" style={{ backgroundColor: '#FFF9E9' }}>
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Apply to Sponsor</h2>
            <p className="text-xl text-gray-600">Let's start the conversation about partnership</p>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Company *</label>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="Company or organization name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none resize-none"
                    placeholder="Tell us about your organization and sponsorship interest..."
                  />
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold py-6 text-lg"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Submit Interest
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section 5: CTA Footer */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Let's build something that leaves a legacy.
          </h2>
          <p className="text-xl mb-10 text-gray-200">
            Partner with us to create moments that transform lives
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg">
              Partner With Us
            </Button>
            <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 border-2 px-10 py-6 text-lg">
              Contact Team
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/attendee-guide" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              Attendee Guide
            </a>
            <span className="text-gray-400">|</span>
            <a href="/faqs" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              FAQs
            </a>
            <span className="text-gray-400">|</span>
            <a href="#contact" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BecomeSponsorPage;
